/**
 * Buyer Location Context
 *
 * Manages the buyer's location for distance calculations.
 * Provides distance calculation to seller locations.
 *
 * Location priority:
 * 1. Manually entered (zip code or address)
 * 2. Shop filter location (buyerLocation)
 * 3. User profile shipping address
 * 4. Browser geolocation (GPS) - if permitted
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { calculateDistance, formatDistance } from '../utils/distance';
import axios from 'axios';

const BuyerLocationContext = createContext();

// Free ZIP code lookup API (no key required)
const ZIP_API_URL = 'https://api.zippopotam.us/us';

// localStorage keys for persisting buyer location and preferences
const STORAGE_KEY = 'buyerLocation';
const DISMISS_KEY = 'locationModalDismissed';
const BROWSE_ALL_KEY = 'browseAllMode';

// Dismissal duration (1 hour in milliseconds)
const DISMISS_DURATION = 60 * 60 * 1000;

// Load from localStorage
const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that coordinates exist
      if (parsed.coordinates?.lat && parsed.coordinates?.lng) {
        console.log('[BuyerLocation] Loaded from localStorage:', parsed);
        return {
          ...parsed,
          loading: false,
          error: null
        };
      }
    }
  } catch (e) {
    console.log('[BuyerLocation] Failed to load from localStorage:', e);
  }
  return null;
};

// Save to localStorage
const saveToStorage = (location) => {
  try {
    if (location.coordinates?.lat && location.coordinates?.lng) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        city: location.city,
        state: location.state,
        postalCode: location.postalCode,
        coordinates: location.coordinates,
        source: location.source
      }));
      console.log('[BuyerLocation] Saved to localStorage');
    }
  } catch (e) {
    console.log('[BuyerLocation] Failed to save to localStorage:', e);
  }
};

// Check if modal was recently dismissed
const checkDismissed = () => {
  try {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      return elapsed < DISMISS_DURATION;
    }
  } catch (e) {
    console.log('[BuyerLocation] Failed to check dismissal:', e);
  }
  return false;
};

// Check if user opted for browse all mode
const checkBrowseAllMode = () => {
  try {
    return localStorage.getItem(BROWSE_ALL_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

export const BuyerLocationProvider = ({ children }) => {
  const [buyerLocation, setBuyerLocation] = useState(() => {
    // Try to load from localStorage first
    const stored = loadFromStorage();
    if (stored) return stored;

    return {
      city: null,
      state: null,
      postalCode: null,
      coordinates: { lat: null, lng: null },
      source: null, // 'manual', 'shop_filter', 'profile', 'gps'
      loading: false,
      error: null
    };
  });

  // Modal and preference states
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(checkDismissed);
  const [isBrowseAllMode, setIsBrowseAllMode] = useState(checkBrowseAllMode);

  const user = useSelector((state) => state.user?.data);

  // Save to localStorage whenever buyerLocation changes
  useEffect(() => {
    if (buyerLocation.coordinates?.lat && buyerLocation.coordinates?.lng) {
      saveToStorage(buyerLocation);
    }
  }, [buyerLocation]);

  // Try to get initial location from user profile
  useEffect(() => {
    if (user?.locationPreference?.coordinates?.lat) {
      setBuyerLocation({
        city: user.locationPreference.city,
        state: user.locationPreference.state,
        postalCode: user.locationPreference.postalCode,
        coordinates: user.locationPreference.coordinates,
        source: user.locationPreference.source || 'profile',
        loading: false,
        error: null
      });
    } else if (user?.shippingAddress?.postalCode) {
      // Try to geocode shipping address
      lookupZipCode(String(user.shippingAddress.postalCode), 'profile');
    }
  }, [user]);

  /**
   * Look up coordinates from a ZIP code
   */
  const lookupZipCode = useCallback(async (zipCode, source = 'manual') => {
    if (!zipCode || typeof zipCode !== 'string') return;

    const normalizedZip = zipCode.trim().substring(0, 5);
    if (!/^\d{5}$/.test(normalizedZip)) {
      setBuyerLocation(prev => ({
        ...prev,
        error: 'Invalid ZIP code format'
      }));
      return;
    }

    setBuyerLocation(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axios.get(`${ZIP_API_URL}/${normalizedZip}`, {
        timeout: 5000
      });

      if (response.data && response.data.places && response.data.places[0]) {
        const place = response.data.places[0];
        const newLocation = {
          city: place['place name'],
          state: place['state abbreviation'],
          postalCode: normalizedZip,
          coordinates: {
            lat: parseFloat(place.latitude),
            lng: parseFloat(place.longitude)
          },
          source,
          loading: false,
          error: null
        };
        console.log('[BuyerLocation] ZIP lookup success:', newLocation);
        setBuyerLocation(newLocation);
      }
    } catch (error) {
      setBuyerLocation(prev => ({
        ...prev,
        loading: false,
        error: error.response?.status === 404
          ? 'ZIP code not found'
          : 'Failed to lookup location'
      }));
    }
  }, []);

  /**
   * Set location from shop filter
   */
  const setFromShopFilter = useCallback((filterLocation) => {
    if (filterLocation?.postalCode) {
      lookupZipCode(filterLocation.postalCode, 'shop_filter');
    } else if (filterLocation?.city && filterLocation?.state) {
      // City/state only - no precise coordinates
      setBuyerLocation({
        city: filterLocation.city,
        state: filterLocation.state,
        postalCode: null,
        coordinates: { lat: null, lng: null },
        source: 'shop_filter',
        loading: false,
        error: null
      });
    }
  }, [lookupZipCode]);

  /**
   * Get location from browser GPS
   */
  const requestGPSLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setBuyerLocation(prev => ({
        ...prev,
        error: 'Geolocation not supported by browser'
      }));
      return;
    }

    setBuyerLocation(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBuyerLocation({
          city: null, // GPS doesn't provide city name directly
          state: null,
          postalCode: null,
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          },
          source: 'gps',
          loading: false,
          error: null
        });
      },
      (error) => {
        let errorMsg = 'Failed to get location';
        if (error.code === error.PERMISSION_DENIED) {
          errorMsg = 'Location permission denied';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMsg = 'Location unavailable';
        } else if (error.code === error.TIMEOUT) {
          errorMsg = 'Location request timed out';
        }
        setBuyerLocation(prev => ({
          ...prev,
          loading: false,
          error: errorMsg
        }));
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  /**
   * Calculate distance to a seller location
   *
   * @param {Object} sellerLocation - Seller's location with coordinates
   * @returns {number|null} - Distance in miles, or null if can't calculate
   */
  const getDistanceToSeller = useCallback((sellerLocation) => {
    if (!buyerLocation.coordinates?.lat || !buyerLocation.coordinates?.lng) {
      console.log('[BuyerLocation] No buyer coordinates available');
      return null;
    }

    // Prefer pickup location coordinates if available (more accurate)
    const sellerCoords = sellerLocation?.pickupLocation?.coordinates?.lat
      ? sellerLocation.pickupLocation.coordinates
      : sellerLocation?.coordinates;

    if (!sellerCoords?.lat || !sellerCoords?.lng) {
      console.log('[BuyerLocation] No seller coordinates available:', sellerLocation);
      return null;
    }

    const distance = calculateDistance(
      buyerLocation.coordinates.lat,
      buyerLocation.coordinates.lng,
      sellerCoords.lat,
      sellerCoords.lng
    );
    console.log('[BuyerLocation] Distance calculated:', distance, 'miles');
    return distance;
  }, [buyerLocation.coordinates]);

  /**
   * Get formatted distance string for display
   *
   * @param {Object} sellerLocation - Seller's location with coordinates
   * @returns {string|null} - Formatted distance like "3.2 mi"
   */
  const getFormattedDistanceToSeller = useCallback((sellerLocation) => {
    const distance = getDistanceToSeller(sellerLocation);
    return formatDistance(distance);
  }, [getDistanceToSeller]);

  /**
   * Clear current location
   */
  const clearLocation = useCallback(() => {
    setBuyerLocation({
      city: null,
      state: null,
      postalCode: null,
      coordinates: { lat: null, lng: null },
      source: null,
      loading: false,
      error: null
    });
    // Also clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.log('[BuyerLocation] Failed to clear localStorage:', e);
    }
  }, []);

  /**
   * Dismiss the location modal for 24 hours
   */
  const dismissModal = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      setIsDismissed(true);
      setShowLocationModal(false);
    } catch (e) {
      console.log('[BuyerLocation] Failed to save dismissal:', e);
    }
  }, []);

  /**
   * Set browse all mode (opts out of location filtering)
   */
  const setBrowseAll = useCallback(() => {
    try {
      localStorage.setItem(BROWSE_ALL_KEY, 'true');
      setIsBrowseAllMode(true);
      setShowLocationModal(false);
    } catch (e) {
      console.log('[BuyerLocation] Failed to save browse all mode:', e);
    }
  }, []);

  /**
   * Clear browse all mode preference
   */
  const clearBrowseAllMode = useCallback(() => {
    try {
      localStorage.removeItem(BROWSE_ALL_KEY);
      setIsBrowseAllMode(false);
    } catch (e) {
      console.log('[BuyerLocation] Failed to clear browse all mode:', e);
    }
  }, []);

  /**
   * Open location modal
   */
  const openLocationModal = useCallback(() => {
    setShowLocationModal(true);
  }, []);

  /**
   * Close location modal
   */
  const closeLocationModal = useCallback(() => {
    setShowLocationModal(false);
  }, []);

  /**
   * Check if should show the welcome modal on homepage
   * Only show if: no location set, not dismissed recently, not in browse all mode
   */
  const shouldShowWelcomeModal = useCallback(() => {
    const hasLoc = !!(buyerLocation.coordinates?.lat && buyerLocation.coordinates?.lng);
    return !hasLoc && !isDismissed && !isBrowseAllMode;
  }, [buyerLocation.coordinates, isDismissed, isBrowseAllMode]);

  /**
   * Get display string for current location
   */
  const getLocationDisplayString = useCallback(() => {
    if (buyerLocation.loading) return 'Getting location...';
    if (buyerLocation.error) return null;

    if (buyerLocation.postalCode) {
      return buyerLocation.city
        ? `${buyerLocation.city}, ${buyerLocation.state} ${buyerLocation.postalCode}`
        : buyerLocation.postalCode;
    }

    if (buyerLocation.city && buyerLocation.state) {
      return `${buyerLocation.city}, ${buyerLocation.state}`;
    }

    if (buyerLocation.coordinates?.lat && buyerLocation.source === 'gps') {
      return 'Current location (GPS)';
    }

    return null;
  }, [buyerLocation]);

  const value = {
    buyerLocation,
    lookupZipCode,
    setFromShopFilter,
    requestGPSLocation,
    getDistanceToSeller,
    getFormattedDistanceToSeller,
    clearLocation,
    getLocationDisplayString,
    hasLocation: !!(buyerLocation.coordinates?.lat && buyerLocation.coordinates?.lng),
    // Modal and preference management
    showLocationModal,
    openLocationModal,
    closeLocationModal,
    dismissModal,
    setBrowseAll,
    isBrowseAllMode,
    clearBrowseAllMode,
    shouldShowWelcomeModal
  };

  return (
    <BuyerLocationContext.Provider value={value}>
      {children}
    </BuyerLocationContext.Provider>
  );
};

export const useBuyerLocation = () => {
  const context = useContext(BuyerLocationContext);
  if (!context) {
    throw new Error('useBuyerLocation must be used within a BuyerLocationProvider');
  }
  return context;
};

export default BuyerLocationContext;
